<section class="region-info" [class.region-info--expanded]="isExpanded" [class.region-info--empty]="!summary">
  <div class="region-info__summary-bar" *ngIf="summary; else emptyState">
    <button class="region-info__toggle" type="button" (click)="toggle()" [attr.aria-expanded]="isExpanded">
      <span class="sr-only">Toggle region info panel</span>
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
        <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.17l3.7-2.94a.75.75 0 11.94 1.16l-4.2 3.34a.75.75 0 01-.94 0l-4.2-3.34a.75.75 0 01.02-1.06z" clip-rule="evenodd" />
      </svg>
    </button>

    <div class="region-info__titles">
      <div class="region-info__primary">{{ primaryTitle }}</div>
      <div class="region-info__secondary" *ngIf="subtitle">{{ subtitle }}</div>
    </div>

    <div class="region-info__meta" *ngIf="summary.regions.length > 1">
      <span>{{ summary.regions.length }} regions</span>
    </div>

    <button
      class="region-info__args-button"
      type="button"
      *ngIf="summary.arguments.length > 0 || hasExecutionDetails"
      (click)="toggleArgs($event)"
      [attr.aria-expanded]="showArgs"
      #argsButton
    >
      Args ({{ summary.arguments.length }})
    </button>

    <div
      class="region-info__args-popover"
      *ngIf="showArgs"
      [ngStyle]="{ top: (argsPosition?.y ?? 0) + 'px', left: (argsPosition?.x ?? 0) + 'px' }"
      #argsPopover
    >
      <div class="region-info__args-popover-header" (pointerdown)="startDrag($event)">
        <span class="region-info__args-popover-title">Execution details</span>
        <button
          type="button"
          class="region-info__args-popover-close"
          (click)="closeArgs($event)"
          (pointerdown)="$event.stopPropagation()"
        >
          <span aria-hidden="true">X</span>
          <span class="sr-only">Close arguments panel</span>
        </button>
      </div>
      <div class="region-info__args-content" *ngIf="summary.executionDetails as details">
        <section class="region-info__args-section" *ngIf="uploadedFileMetadata$ | async as file">
          <h3>Source file</h3>
          <a
            class="region-info__file-link"
            [href]="file.jsonViewUrl"
            target="_blank"
            rel="noopener noreferrer"
            [title]="'Open ' + file.fileName"
          >
            {{ file.fileName }}
          </a>
        </section>

        <section class="region-info__args-section" *ngIf="details.summary.length > 0">
          <h3>Run summary</h3>
          <dl class="region-info__details-list">
            <div *ngFor="let detail of details.summary; trackBy: trackByDetail">
              <dt>{{ detail.label }}</dt>
              <dd>{{ detail.value }}</dd>
            </div>
          </dl>
        </section>

        <section
          class="region-info__args-section"
          *ngIf="hasRunMatrix"
        >
          <h3>Run matrix</h3>
          <div class="region-info__run-matrix">
            <div class="region-info__run-matrix-row" *ngIf="details.rankValues.length > 0">
              <span>MPI ranks</span>
              <div class="region-info__value-chips">
                <span *ngFor="let value of details.rankValues; let i = index; trackBy: trackByValue">{{ value }}</span>
              </div>
            </div>
            <div class="region-info__run-matrix-row" *ngIf="details.coreValues.length > 0">
              <span>{{ coreLabel }}</span>
              <div class="region-info__value-chips">
                <span *ngFor="let value of details.coreValues; let i = index; trackBy: trackByValue">{{ value }}</span>
              </div>
            </div>
            <div class="region-info__run-matrix-row" *ngIf="details.inputValues.length > 0">
              <span>Inputs</span>
              <div class="region-info__value-chips">
                <span *ngFor="let value of details.inputValues; let i = index; trackBy: trackByValue">{{ value }}</span>
              </div>
            </div>
            <div class="region-info__run-matrix-row" *ngIf="details.repetitionValues.length > 0">
              <span>Repetitions</span>
              <div class="region-info__value-chips">
                <span *ngFor="let value of details.repetitionValues; let i = index; trackBy: trackByValue">{{ value }}</span>
              </div>
            </div>
          </div>
        </section>

        <section class="region-info__args-section" *ngIf="details.flags.length > 0">
          <h3>Flags and settings</h3>
          <dl class="region-info__details-list">
            <div *ngFor="let detail of details.flags; trackBy: trackByDetail">
              <dt>{{ detail.label }}</dt>
              <dd>{{ detail.value }}</dd>
            </div>
          </dl>
        </section>

        <section class="region-info__args-section" *ngIf="details.command">
          <h3>Command</h3>
          <code class="region-info__command">{{ details.command }}</code>
        </section>

        <section class="region-info__args-section" *ngIf="summary.arguments.length > 0">
          <h3>Arguments</h3>
          <ul class="region-info__args-list">
            <li *ngFor="let arg of summary.arguments; let i = index">i{{ i + 1 }} = {{ arg }}</li>
          </ul>
        </section>
      </div>
    </div>
  </div>

  <div class="region-info__overlay" *ngIf="summary && isExpanded">
    <div class="region-info__overlay-content">
      <div class="region-info__region-grid">
        <article class="region-card" *ngFor="let region of summary.regions; trackBy: trackByRegion">
          <header class="region-card__header">
            <span class="region-card__badge">{{ region.regionId }}</span>
            <div class="region-card__text">
              <span class="region-card__title">{{ region.label }}</span>
              <span class="region-card__subtitle">{{ region.filename }}</span>
            </div>
          </header>
          <dl class="region-card__stats">
            <div>
              <dt>Code Lines</dt>
              <dd>{{ region.lines }}</dd>
            </div>
            <div *ngIf="region.executionRange">
              <dt>% Exec</dt>
              <dd>{{ region.executionRange[0] | number:'1.0-1' }} – {{ region.executionRange[1] | number:'1.0-1' }}</dd>
            </div>
            <div *ngIf="region.timeVariationRange">
              <dt>ΔT</dt>
              <dd>{{ region.timeVariationRange[0] | number:'1.0-1' }} – {{ region.timeVariationRange[1] | number:'1.0-1' }}</dd>
            </div>
          </dl>
        </article>
      </div>
    </div>
  </div>
</section>

<ng-template #emptyState>
  <div class="region-info__summary-bar">
    <div class="region-info__titles">
      <div class="region-info__primary">No region selected</div>
      <div class="region-info__secondary">Select a region to populate details</div>
    </div>
  </div>
</ng-template>
